import { useEffect } from 'react';
import { Modal, Form, Input, Select, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import {
  updateTemplateContextAction,
  listTemplateContextsAction,
} from '../../../../store/actions/templatecontext.action';
import {
  ENCOUNTER_PHASE_OPTIONS,
  criteriaToRows,
  rowsToCriteria,
  type TemplateContextRecord,
  type TemplateContextFormValues,
  type CriteriaRow,
} from './templateContext.constants';
import CriteriaFields from './CriteriaFields';

type EditTemplateContextProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  record: TemplateContextRecord | null;
  filters?: Record<string, string>;
};

const EditTemplateContext = ({
  isOpen,
  setIsOpen,
  record,
  filters,
}: EditTemplateContextProps) => {
  const [form] = Form.useForm<TemplateContextFormValues>();
  const dispatch = useDispatch() as (action: unknown) => unknown;

  useEffect(() => {
    if (isOpen && record) {
      form.setFieldsValue({
        encounter_phase: record.encounter_phase,
        variable_name: record.variable_name,
        template_name: record.template_name,
        data: record.data,
        section_name: record.section_name,
        criteria: criteriaToRows(record.matching_criteria),
      });
    }
  }, [isOpen, record, form]);

  const handleClose = () => {
    form.resetFields();
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    if (!record) return;
    const values = await form.validateFields();
    const criteriaRows: CriteriaRow[] = values.criteria ?? [];
    const matchingCriteria = rowsToCriteria(criteriaRows);

    const payload = {
      encounter_phase: values.encounter_phase,
      variable_name: values.variable_name?.trim(),
      template_name: values.template_name?.trim(),
      data: values.data?.trim(),
      matching_criteria: matchingCriteria,
      section_name: values.section_name?.trim(),
    };

    dispatch(
      updateTemplateContextAction(record.template_context_id, payload, () => {
        handleClose();
        dispatch(listTemplateContextsAction(filters));
      })
    );
  };

  return (
    <Modal
      title="Edit Template Context"
      width={800}
      open={isOpen}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Save Changes"
      destroyOnClose
    >
      <Form
        layout="horizontal"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        style={{ marginTop: 16 }}
        form={form}
        initialValues={{ criteria: [] }}
      >
        <Form.Item
          label={
            <span>
              Template Name{' '}
              <Tooltip title="Supports partial match — e.g. 'Well' will match 'Well Visit'">
                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
              </Tooltip>
            </span>
          }
          name="template_name"
          rules={[{ required: true, message: 'Template name is required' }]}
        >
          <Input placeholder="e.g. Well Visit" />
        </Form.Item>

        <Form.Item
          label="Encounter Phase"
          name="encounter_phase"
          rules={[{ required: true, message: 'Encounter phase is required' }]}
        >
          <Select
            placeholder="Select encounter phase"
            options={ENCOUNTER_PHASE_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          label={
            <span>
              Section Name{' '}
              <Tooltip title="Exact match, case-insensitive">
                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
              </Tooltip>
            </span>
          }
          name="section_name"
          rules={[{ required: true, message: 'Section name is required' }]}
        >
          <Input placeholder="e.g. Assessment" />
        </Form.Item>

        <Form.Item
          label={
            <span>
              Variable Name{' '}
              <Tooltip title="Use {{name}} to substitute in template section">
                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
              </Tooltip>
            </span>
          }
          name="variable_name"
          rules={[{ required: true, message: 'Variable name is required' }]}
        >
          <Input placeholder="e.g. my_var" />
        </Form.Item>

        <Form.Item
          label="Instructions"
          name="data"
          rules={[{ required: true, message: 'Instructions are required' }]}
        >
          <Input.TextArea
            rows={12}
            placeholder="Template context instructions"
          />
        </Form.Item>

        <CriteriaFields />
      </Form>
    </Modal>
  );
};

export default EditTemplateContext;
